import React from "react";
import { ShoppingBag, Shield, HeartPulse } from "lucide-react";
import { CTA_PRICE } from "../../config/pricing";
import { useLanguage } from "../../contexts/LanguageContext";

const HowItWorksSection = ({ onGetQRCode, onViewDemo }) => {
  const { t } = useLanguage();
  const steps = [
    {
      number: 1,
      titleKey: "howItWorks.step1TitleAlt",
      descKey: "howItWorks.step1DescAlt",
      icon: ShoppingBag,
    },
    {
      number: 2,
      titleKey: "howItWorks.step2TitleAlt",
      descKey: "howItWorks.step2DescAlt",
      icon: Shield,
    },
    {
      number: 3,
      titleKey: "howItWorks.step3TitleAlt",
      descKey: "howItWorks.step3DescAlt",
      icon: HeartPulse,
    },
  ];

  return (
    <section className="w-full bg-white px-4 py-5 md:px-6 md:py-6 lg:py-8">
      <div className="mx-auto max-w-7xl">
        {/* Section heading */}
        <h2 className="text-center text-2xl font-bold text-gray-900 md:text-3xl lg:text-4xl">
          {t("howItWorks.titleShort")}
        </h2>

        {/* Stat badge */}
        <div className="mx-auto mt-4 flex justify-center">
          <span className="inline-flex items-center rounded-full bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700">
            {t("howItWorks.setupBadge")}
          </span>
        </div>

        {/* How it Works - 3 steps */}
        <div className="mt-6 grid grid-cols-1 gap-6 md:mt-8 md:grid-cols-3">
          {steps.map((step) => {
            const Icon = step.icon;
            return (
              <div
                key={step.number}
                className="relative rounded-lg bg-white p-5 shadow-md"
              >
                <span className="absolute -top-3 -left-3 flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-sm font-bold text-white">
                  {step.number}
                </span>
                <div className="mt-2 flex flex-col items-center text-center">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600">
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t(step.titleKey)}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">
                    {t(step.descKey)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Action buttons */}
        <div className="mt-6 flex flex-col items-center justify-center gap-4 sm:flex-row md:mt-8">
          <button
            type="button"
            onClick={onGetQRCode}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-6 py-3.5 text-base font-semibold text-white shadow-md transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:w-auto"
          >
            <ShoppingBag className="h-5 w-5" aria-hidden="true" />
            {t("howItWorks.step1Cta")} — {CTA_PRICE}
          </button>
          <button
            type="button"
            onClick={onViewDemo}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg border-2 border-gray-300 bg-white px-6 py-3.5 text-base font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 sm:w-auto"
          >
            {t("howItWorks.viewDemo")}
          </button>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
